/**
 * Composant AddEditDialog - Dialogue pour ajouter ou modifier un prospect
 * Inclut: informations personnelles, adresse, et formation
 */

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { OriginePicker } from '../OriginePicker';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../ui/tabs';

export const AddEditDialog = ({
  open,
  onOpenChange,
  isEdit,
  formData,
  setFormData,
  config,
  onSave,
  onCancel
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier le prospect' : 'Nouveau prospect'}</DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="info" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="info">Informations</TabsTrigger>
            <TabsTrigger value="adresse">Adresse</TabsTrigger>
            <TabsTrigger value="formation">Formation</TabsTrigger>
          </TabsList>
          
          <TabsContent value="info" className="space-y-4 mt-4">
            <OriginePicker formData={formData} setFormData={setFormData} />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="prenom">Prénom *</Label>
                <Input
                  id="prenom"
                  value={formData.prenom}
                  onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                  data-testid="input-prenom"
                />
              </div>
              <div>
                <Label htmlFor="nom">Nom *</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  data-testid="input-nom"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email">Email <span className="text-xs font-normal text-slate-400">(facultatif)</span></Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                data-testid="input-email"
              />
            </div>
            
            <div>
              <Label htmlFor="telephone">Téléphone</Label>
              <Input
                id="telephone"
                value={formData.telephone}
                onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                data-testid="input-telephone"
              />
            </div>
            
            <div>
              <Label htmlFor="statut">Statut</Label>
              <Select 
                value={formData.statut} 
                onValueChange={(val) => setFormData({...formData, statut: val})}
              >
                <SelectTrigger data-testid="select-statut">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {config.statuts.filter(s => !['stagiaire', 'client', 'client_dormant'].includes(s.value) || s.value === formData.statut).map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="recherche_financement"
                checked={formData.recherche_financement}
                onCheckedChange={(checked) => setFormData({...formData, recherche_financement: checked})}
                data-testid="checkbox-financement"
              />
              <Label htmlFor="recherche_financement">En recherche de financement</Label>
            </div>
            
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                data-testid="textarea-notes"
              />
            </div>
          </TabsContent>
          
          <TabsContent value="adresse" className="space-y-4 mt-4">
            <div>
              <Label htmlFor="adresse">Adresse</Label>
              <Input
                id="adresse"
                value={formData.adresse}
                onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                data-testid="input-adresse"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="code_postal">Code postal</Label>
                <Input
                  id="code_postal"
                  value={formData.code_postal}
                  onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                  data-testid="input-code-postal"
                />
              </div>
              <div>
                <Label htmlFor="ville">Ville</Label>
                <Input
                  id="ville"
                  value={formData.ville}
                  onChange={(e) => setFormData({...formData, ville: e.target.value})}
                  data-testid="input-ville"
                />
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="formation" className="space-y-4 mt-4">
            <div>
              <Label htmlFor="diplome">Diplôme souhaité</Label>
              <Input
                id="diplome"
                value={formData.diplome_souhaite}
                onChange={(e) => setFormData({...formData, diplome_souhaite: e.target.value})}
                data-testid="input-diplome"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="centre">Centre de formation</Label>
                <Select 
                  value={formData.centre || 'atlantique'} 
                  onValueChange={(val) => setFormData({...formData, centre: val})}
                >
                  <SelectTrigger data-testid="select-centre">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="atlantique">Centre Atlantique</SelectItem>
                    <SelectItem value="occitanie">Centre Occitanie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Session</Label>
                <Select value={formData.session || 'auto'} onValueChange={(v) => setFormData({...formData, session: v === 'auto' ? '' : v})}>
                  <SelectTrigger data-testid="select-session">
                    <SelectValue placeholder="Automatique (année en cours)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automatique (année en cours)</SelectItem>
                    {Array.from(new Set([...Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - 2 + i)), formData.session].filter(Boolean))).sort().map(a => (
                      <SelectItem key={a} value={a}>Session {a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="date_debut">Date de début souhaitée</Label>
                <Input
                  id="date_debut"
                  type="date"
                  value={formData.date_debut_souhaitee}
                  onChange={(e) => setFormData({...formData, date_debut_souhaitee: e.target.value})}
                  data-testid="input-date-debut"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="envoye_par">Envoyé par</Label>
                <Input
                  id="envoye_par"
                  value={formData.envoye_par || ''}
                  onChange={(e) => setFormData({...formData, envoye_par: e.target.value})}
                  placeholder="Nom de la personne..."
                  data-testid="input-envoye-par"
                />
              </div>
              <div>
                <Label htmlFor="entreprise_stage">Entreprise de stage</Label>
                <Input
                  id="entreprise_stage"
                  value={formData.entreprise_stage || ''}
                  onChange={(e) => setFormData({...formData, entreprise_stage: e.target.value})}
                  placeholder="Si connue..."
                  data-testid="input-entreprise-stage"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="mode_financement">Mode de financement</Label>
              <Select 
                value={formData.mode_financement} 
                onValueChange={(val) => setFormData({...formData, mode_financement: val})}
              >
                <SelectTrigger data-testid="select-financement">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto_finance">Auto-financé</SelectItem>
                  <SelectItem value="cfa">CFA / Apprentissage</SelectItem>
                  <SelectItem value="opca">OPCA / OPCO</SelectItem>
                  <SelectItem value="cpf">CPF</SelectItem>
                  <SelectItem value="france_travail">France Travail</SelectItem>
                  <SelectItem value="region">Région</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Blocs de compétences (intitulés personnalisables)</Label>
              <div className="space-y-3 mt-2">
                {config.blocs_competences.filter(bc => bc.code !== 'BC4').map(bc => (
                  <div key={bc.code} className="space-y-1 p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={bc.code}
                        checked={formData.bc_selection.includes(bc.code)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({...formData, bc_selection: [...formData.bc_selection, bc.code]});
                          } else {
                            setFormData({...formData, bc_selection: formData.bc_selection.filter(c => c !== bc.code)});
                          }
                        }}
                      />
                      <Label htmlFor={bc.code} className="text-sm font-medium">
                        {bc.code}
                      </Label>
                    </div>
                    <Input
                      value={formData.bc_intitules?.[bc.code] || bc.nom}
                      onChange={(e) => setFormData({
                        ...formData, 
                        bc_intitules: {...(formData.bc_intitules || {}), [bc.code]: e.target.value}
                      })}
                      className="text-sm"
                      placeholder={`Intitulé du ${bc.code}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label htmlFor="ligne_additionnelle">Module additionnel (ligne libre)</Label>
              <Input
                id="ligne_additionnelle"
                value={formData.ligne_additionnelle}
                onChange={(e) => setFormData({...formData, ligne_additionnelle: e.target.value})}
                placeholder="Ex: Formation complémentaire..."
                data-testid="input-ligne-additionnelle"
              />
            </div>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onCancel}>
            Annuler
          </Button>
          <Button 
            onClick={onSave}
            className="bg-[#8DC63F] hover:bg-[#7AB62F]"
            data-testid="save-prospect-btn"
          >
            {isEdit ? 'Mettre à jour' : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditDialog;
